/**
 * Tenant picker in the sidebar footer.
 *
 * Switching is local state only — no request is scoped by workspace yet, so
 * selecting one changes the label and nothing else. Kept as a real control
 * rather than a static label so the tenancy work has a UI to plug into.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useTranslation } from '../../context/LanguageContext';
import { MOCK_WORKSPACES, DEFAULT_WORKSPACE_ID } from '../../config/workspaces';
import type { Workspace } from '../../config/workspaces';

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(DEFAULT_WORKSPACE_ID);
  const containerRef = useRef<HTMLDivElement>(null);

  const active = MOCK_WORKSPACES.find((w) => w.id === activeId) ?? MOCK_WORKSPACES[0];

  // A dropdown anchored in the sidebar overlaps the page, so it has to close on
  // an outside click or it stays over content the user is trying to reach.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="w-full flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left transition-colors hover:border-slate-300 hover:bg-slate-50"
      >
        <Monogram workspace={active} />
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {t('workspace.label')}
          </span>
          <span className="block truncate text-[13px] font-semibold text-slate-800">
            {active.name}
          </span>
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute bottom-full left-0 z-30 mb-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-slate-900/10"
        >
          {MOCK_WORKSPACES.map((workspace) => {
            const isActive = workspace.id === activeId;
            return (
              <button
                key={workspace.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setActiveId(workspace.id);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-2.5 py-2 text-left transition-colors hover:bg-slate-50"
              >
                <Monogram workspace={workspace} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-slate-800">
                    {workspace.name}
                  </span>
                  <span className="block text-[11px] text-slate-400">
                    {t('workspace.members', { count: workspace.memberCount })}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                    {t(workspace.role === 'admin' ? 'workspace.roleAdmin' : 'workspace.roleMember')}
                  </span>
                  {isActive && <Check className="h-3.5 w-3.5 text-blue-600" />}
                </span>
              </button>
            );
          })}

          <div className="my-1 border-t border-slate-100" />

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2 px-3 py-2 text-[13px] font-semibold text-blue-600 transition-colors hover:bg-blue-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('workspace.create')}
          </button>
        </div>
      )}
    </div>
  );
}

function Monogram({ workspace }: Readonly<{ workspace: Workspace }>) {
  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-[10px] font-bold text-white',
        workspace.accentClass
      )}
    >
      {workspace.initials}
    </span>
  );
}
